import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

export function matchSportSet(related, options) {
  return Object.entries(related).map(([key, product]) => ({
    key, product, variant: product.variants.find(item => item.options.length === options.length && item.options.every((option, index) => option === options[index])),
  }));
}

export function sportSetTotal(matches, discounted) {
  if (matches.length !== 3 || matches.some(item => !item.variant)) return null;
  return matches.reduce((total, item) => total + (discounted ? item.variant.cents - Math.round(item.variant.cents * 0.05) : item.variant.cents), 0);
}

// Resolve Horizon's cart promise with the full cart, including rejected/partial adds.
export async function addSportPieces({ items, routes, sections, start, fetcher = fetch }) {
  const pending = start();
  let accepted = false;
  try {
    const response = await fetcher(routes.add, {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections, sections_url: location.pathname }),
    });
    const result = await response.json();
    const failed = !response.ok || Boolean(result.status);
    accepted = !failed;
    const refresh = await fetcher(routes.cart, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!refresh.ok) throw new Error('Cart refresh failed');
    const cart = await refresh.json();
    pending.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
      items: cart.items, sections: result.sections, didError: failed, source: 'eb-sport-set', itemCount: cart.item_count,
    } });
    return { state: failed ? 'rejected' : 'added', message: typeof result.description === 'string' ? result.description : '' };
  } catch (error) {
    pending.reject(error);
    return { state: accepted ? 'added-unconfirmed-cart' : 'unconfirmed' };
  }
}

class SportProduct extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.data = JSON.parse(this.querySelector('[data-sport-gallery-data]').textContent);
    this.variant = this.data.variants.find((variant) => variant.id === this.data.selected);
    this.setOptions = null;
    this.image = this.querySelector('.eb-sport-main-image');
    this.zoomButton = this.querySelector('[data-sport-zoom]');
    this.thumbs = [...this.querySelectorAll('[data-sport-thumb]')];
    this.addEventListener('click', (event) => {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.matches('[data-set-option]') && !this.pending) {
        this.setOptions[Number(target.dataset.setOption)] = target.value;
        this.renderSet();
      }
      if (target.matches('[data-sport-add]')) this.addPieces(target.dataset.sportAdd);
      if (target.matches('[data-sport-thumb]')) this.showThumb(target);
      if (target.matches('[data-sport-size-guide]')) document.getElementById(target.getAttribute('aria-controls'))?.showModal();
      if (target.matches('[data-sport-close]')) target.closest('dialog')?.close();
      if (target.matches('[data-sport-zoom]')) {
        const dialog = this.querySelector('.eb-sport-zoom-dialog');
        const image = dialog.querySelector('img');
        image.src = target.dataset.zoomSrc;
        image.alt = this.image.alt;
        dialog.showModal();
      }
      if (target.getAttribute('aria-disabled') === 'true') event.preventDefault();
    }, options);
    this.addEventListener('change', (event) => {
      if (event.target.closest('variant-picker')?.dataset.productId === this.dataset.productId) this.updateSelection();
    }, options);
    this.addEventListener(StandardEvents.productSelect, (event) => {
      if (event.target.closest('product-card')) return;
      event.promise.then(({ detail }) => {
        if (detail?.productId && String(detail.productId) !== this.dataset.productId) return;
        this.updateSelection();
      }).catch(() => { /* The native variant picker owns request error handling. */ });
    }, options);
    this.addEventListener('keydown', (event) => {
      if (!event.target.matches('[data-sport-thumb]') || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.thumbs.length - 1 : (this.thumbs.indexOf(event.target) + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + this.thumbs.length) % this.thumbs.length;
      this.thumbs[next].focus();
      this.showThumb(this.thumbs[next]);
    }, options);
    this.zoomButton.addEventListener('pointerdown', (event) => { this.pointerStart = [event.clientX, event.clientY]; }, options);
    this.zoomButton.addEventListener('pointerup', (event) => {
      if (!this.pointerStart || this.thumbs.length < 2) return;
      const dx = event.clientX - this.pointerStart[0];
      const dy = event.clientY - this.pointerStart[1];
      this.pointerStart = null;
      if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;
      const current = this.thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true');
      this.showThumb(this.thumbs[(current + (dx < 0 ? 1 : -1) + this.thumbs.length) % this.thumbs.length]);
      this.zoomButton.addEventListener('click', (click) => { click.preventDefault(); click.stopPropagation(); }, { once: true });
    }, options);
    this.updateSelection();
  }

  disconnectedCallback() { this.controller?.abort(); }

  responsiveSources(src) {
    return [400, 600, 800, 1000, 1200].map((width) => {
      const url = new URL(src, location.origin);
      url.searchParams.set('width', width);
      return `${url.href} ${width}w`;
    }).join(', ');
  }

  showThumb(thumb) {
    if (!this.variant) return;
    const isVariant = !thumb || thumb.dataset.sportThumb === 'variant';
    this.image.src = isVariant ? this.variant.src : thumb.dataset.src;
    if (isVariant) this.image.srcset = this.responsiveSources(this.variant.src);
    else this.image.removeAttribute('srcset');
    this.image.alt = isVariant ? `${this.querySelector('h1').textContent} - ${this.variant.options[0]}` : thumb.getAttribute('aria-label');
    this.zoomButton.dataset.zoomSrc = isVariant ? this.variant.zoom : thumb.dataset.zoomSrc;
    this.classList.toggle('eb-sport-detail-active', !isVariant);
    for (const button of this.thumbs) button.setAttribute('aria-pressed', String(button === thumb || (!thumb && button.dataset.sportThumb === 'variant')));
  }

  updateSelection() {
    const picker = this.querySelector('variant-picker');
    if (!picker) return;
    const options = [...picker.querySelectorAll('input[type="radio"]:checked, select')].map((input) => input.value);
    const variant = this.data.variants.find((item) => item.options.every((option, index) => option === options[index]));
    if (!variant) return;
    const changed = this.variant?.id !== variant.id;
    this.variant = variant;
    if (changed || !this.setOptions) this.setOptions = [...variant.options];
    const legend = picker.querySelector('fieldset[data-fieldset-index="0"] legend');
    if (legend) legend.textContent = `Culoare: ${options[0]}`;
    const thumbnail = this.querySelector('[data-sport-thumb="variant"] img');
    if (thumbnail) {
      thumbnail.src = variant.thumb;
      thumbnail.removeAttribute('srcset');
      thumbnail.alt = options[0];
    }
    if (changed || !this.initialized) this.showThumb(this.thumbs[0]);
    this.initialized = true;
    const discount = this.querySelector('[data-sport-discount]');
    discount.hidden = !(variant.compare > variant.price);
    discount.textContent = variant.compare > variant.price ? `-${Math.round((variant.compare - variant.price) * 100 / variant.compare)}%` : '';
    for (const link of this.querySelectorAll('[data-related-key]')) {
      const target = this.data.related[link.dataset.relatedKey];
      if (!target) continue;
      const match = target.variants.find((item) => item.options.every((option, index) => option === options[index]));
      if (!match) {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        continue;
      }
      const url = new URL(target.url, location.origin);
      url.searchParams.set('variant', match.id);
      const current = new URL(location.href);
      for (const parameter of ['view', 'preview_theme_id']) if (current.searchParams.has(parameter)) url.searchParams.set(parameter, current.searchParams.get(parameter));
      link.href = url.pathname + url.search;
      link.removeAttribute('aria-disabled');
      const image = link.querySelector('img');
      image.src = match.image;
      image.removeAttribute('srcset');
      link.querySelector('.eb-sport-related-price').textContent = match.price;
    }
    this.renderSet();
  }

  renderSet() {
    const set = this.querySelector('[data-sport-set]');
    if (!set || !this.variant) return;
    this.setMatches = matchSportSet(this.data.related, this.setOptions);
    const choice = set.querySelector('[data-set-choice]');
    if (choice) choice.textContent = this.setOptions.join(' / ');
    for (const { key, product, variant } of this.setMatches) {
      for (const link of set.querySelectorAll('[data-set-link]')) {
        if (link.dataset.setLink !== key) continue;
        if (variant) {
          const url = new URL(product.url, location.origin);
          url.searchParams.set('variant', variant.id);
          const current = new URL(location.href);
          for (const parameter of ['view', 'preview_theme_id']) if (current.searchParams.has(parameter)) url.searchParams.set(parameter, current.searchParams.get(parameter));
          link.href = url.pathname + url.search;
          link.removeAttribute('aria-disabled');
          const image = link.querySelector('img');
          if (image) {
            const source = new URL(variant.image, location.origin);
            source.searchParams.set('width', link.classList.contains('eb-sport-set-photo') ? '720' : '160');
            image.src = source.href;
            image.removeAttribute('srcset');
          }
        } else {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
        }
      }
      const piece = [...set.querySelectorAll('[data-set-piece]')].find(el => el.dataset.setPiece === key);
      if (!piece) continue;
      const priceLabel = piece.querySelector('[data-set-item-price]');
      if (priceLabel) priceLabel.textContent = variant?.price || '';
      const variantLabel = piece.querySelector('[data-set-variant]');
      if (variantLabel) variantLabel.textContent = this.setOptions.join(' / ');
      piece.querySelector('[data-set-availability]').textContent = variant?.available ? '\u00cen stoc' : 'Stoc epuizat';
      piece.querySelector('[data-sport-add]').disabled = this.pending || !variant?.available;
      for (const button of piece.querySelectorAll('[data-set-option]')) {
        const index = Number(button.dataset.setOption);
        button.setAttribute('aria-pressed', String(this.setOptions[index] === button.value));
        button.disabled = Boolean(this.pending);
        if (index === 1) {
          const sizeVariant = product.variants.find(item => item.options[0] === this.setOptions[0] && item.options[1] === button.value);
          button.dataset.unavailable = String(!sizeVariant?.available);
          button.setAttribute('aria-label', button.value + (sizeVariant?.available ? '' : ' - Stoc epuizat'));
        }
      }
    }
    for (const label of set.querySelectorAll('[data-set-color]')) label.textContent = this.setOptions[0];
    for (const label of set.querySelectorAll('[data-set-size]')) label.textContent = this.setOptions[1];
    const canAdd = this.setMatches.length === 3 && this.setMatches.every(item => item.variant?.available);
    const allButton = set.querySelector('[data-sport-add="all"]');
    allButton.disabled = this.pending || !canAdd;
    allButton.querySelector('span').textContent = this.pending ? 'Se adaug\u0103...' : canAdd ? 'Adaug\u0103 setul \u00een co\u0219' : 'Set indisponibil \u00een aceast\u0103 combina\u021bie';
    const active = this.data.setDiscountActive === true;
    const total = sportSetTotal(this.setMatches, active);
    const money = value => new Intl.NumberFormat('ro-RO', { style: 'currency', currency: this.data.currency || 'RON' }).format(value / 100);
    set.querySelector('[data-set-price]').textContent = total === null ? '' : money(total);
    const original = set.querySelector('[data-set-original]');
    original.hidden = !active || total === null;
    original.textContent = total === null ? '' : money(sportSetTotal(this.setMatches, false));
    for (const offer of set.querySelectorAll('[data-set-offer]')) offer.hidden = !active;
    set.querySelector('[data-set-terms]').hidden = !active;
  }

  async addPieces(key) {
    if (this.pending || !this.variant) return;
    const matches = matchSportSet(this.data.related, this.setOptions);
    const chosen = key === 'all' ? matches : matches.filter(item => item.key === key);
    if (chosen.length !== (key === 'all' ? 3 : 1) || chosen.some(item => !item.variant?.available)) return;
    const items = chosen.map(item => ({ id: item.variant.id, quantity: 1 }));
    const status = this.querySelector('[data-set-status]');
    this.pending = true;
    status.textContent = '';
    this.querySelector('[data-sport-set]').setAttribute('aria-busy', 'true');
    this.renderSet();
    const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component')).map(el => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const result = await addSportPieces({ items, routes: this.data.routes, sections, start: () => {
      const deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise }));
      return deferred;
    } });
    this.pending = false;
    if (!this.isConnected) return;
    this.querySelector('[data-sport-set]').removeAttribute('aria-busy');
    this.renderSet();
    status.textContent = result.state === 'added' ? (items.length === 3 ? 'Cele 3 piese au fost ad\u0103ugate \u00een co\u0219.' : 'Produsul a fost ad\u0103ugat \u00een co\u0219.') : result.state === 'rejected' ? `${result.message || 'Stocul disponibil nu permite aceast\u0103 cantitate.'} Verific\u0103 produsele din co\u0219.` : 'Verific\u0103 co\u0219ul \u00eenainte de a re\u00eencerca. Nu am repetat ad\u0103ugarea.';
    status.dataset.error = result.state === 'added' ? 'false' : 'true';
    this.querySelector('[data-set-cart]').hidden = false;
  }
}
if (!customElements.get('eb-sport-product')) customElements.define('eb-sport-product', SportProduct);
